/**
 * Sorting Confirmation Modal
 * Shows when user applies a sort that will change the custom order
 */

import { sortingState } from './state.js';

class SortingModal {
  constructor() {
    this.modal = null;
    this.isOpen = false;
    this.pendingCallback = null;
  }

  /**
   * Initialize the sorting modal
   */
  init() {
    this.createModal();
    this.attachEventListeners();
  }

  /**
   * Create the sorting modal HTML
   */
  createModal() {
    const modalHTML = `
      <div id="sortingModal" class="sorting-modal" style="display: none;">
        <div class="sorting-modal-backdrop"></div>
        <div class="sorting-modal-content">
          <button class="sorting-modal-close">&times;</button>

          <h2 class="sorting-modal-title">Overwrite Custom Order?</h2>
          <p class="sorting-modal-description">
            Reordering tracks while using a sorted view will overwrite your saved custom order.
            The new arrangement will become your custom order.
          </p>

          <label class="sorting-modal-checkbox">
            <input type="checkbox" id="sortingDontAskAgain">
            <span>Don't ask again</span>
          </label>

          <div class="sorting-modal-actions">
            <button class="btn-cancel" id="sortingCancelBtn">Cancel</button>
            <button class="btn-confirm" id="sortingConfirmBtn">Overwrite</button>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
    this.modal = document.getElementById('sortingModal');
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Close modal
    this.modal.querySelector('.sorting-modal-backdrop').addEventListener('click', () => this.cancel());
    this.modal.querySelector('.sorting-modal-close').addEventListener('click', () => this.cancel());
    this.modal.querySelector('#sortingCancelBtn').addEventListener('click', () => this.cancel());

    // Confirm button
    this.modal.querySelector('#sortingConfirmBtn').addEventListener('click', () => this.confirm());

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.cancel();
      }
    });
  }

  /**
   * Show the sorting modal
   * @returns {Promise<boolean>} - Resolves true if confirmed, false if cancelled
   */
  show() {
    // If "don't ask again" is set, just confirm immediately
    if (sortingState.dontAskAgain) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      this.pendingCallback = () => {
        resolve(true);
      };
      this.pendingReject = () => {
        resolve(false);
      };

      // Reset checkbox
      this.modal.querySelector('#sortingDontAskAgain').checked = false;

      this.modal.style.display = 'flex';
      this.isOpen = true;
    });
  }

  /**
   * Confirm the action
   */
  confirm() {
    // Check if "don't ask again" is checked
    const dontAskAgain = this.modal.querySelector('#sortingDontAskAgain').checked;
    if (dontAskAgain) {
      sortingState.dontAskAgain = true;
    }

    this.hide();
    if (this.pendingCallback) {
      this.pendingCallback();
      this.pendingCallback = null;
    }
    this.pendingReject = null;
  }

  /**
   * Cancel the action
   */
  cancel() {
    this.hide();
    if (this.pendingReject) {
      this.pendingReject();
      this.pendingReject = null;
    }
    this.pendingCallback = null;
  }

  /**
   * Hide the sorting modal
   */
  hide() {
    this.modal.style.display = 'none';
    this.isOpen = false;
  }
}

// Export singleton instance
export const sortingModal = new SortingModal();
